import moment from "moment";
import { typeOfPAF } from "../../../../constants/typeOfPAF";
import {
  isDispensingMoreThan3DaysBetween2Dates,
  isDispensingMoreThan7DaysBetween2Dates,
} from "../../../../shared/helpers";

/** Inpatient pharmacist may edit while the PAF is in one of these statuses. */
export const INPATIENT_PHARMACY_EDITABLE_PAF_STATUSES = [
  "Submitted",
  "Expiring",
  "Overdue",
];

export function getPafStatus(form) {
  if (form == null) return "";
  return form.status_name ?? form.status ?? "";
}

/** True when a dispensing brand has been selected (not awaiting dispense). */
export function hasDispensingBrandAssigned(form) {
  const holder = form?.marketing_holder;
  if (holder == null || String(holder).trim() === "") return false;
  return String(holder).trim() !== "Awaiting to be Dispensed";
}

export function isInpatientPharmacyEditablePafStatus(form) {
  return INPATIENT_PHARMACY_EDITABLE_PAF_STATUSES.includes(getPafStatus(form));
}

/**
 * Legacy prop name `hideEditButton`: when true, pharmacy EDIT controls are shown.
 */
export function canInpatientPharmacistEditPaf({ isPharmacy, form, findProduct }) {
  if (!isPharmacy || !isInpatientPharmacyEditablePafStatus(form)) {
    return false;
  }
  if (findProduct == null) {
    return true;
  }
  return (
    findProduct.can_renew_registration === true &&
    findProduct.product_active === true
  );
}

/** Align `status` and `status_name` when the API returns only one of them. */
export function normalizePafPrescription(data) {
  if (data == null || typeof data !== "object") {
    return data;
  }
  const status = data.status_name ?? data.status;
  if (status == null) {
    return data;
  }
  return {
    ...data,
    status,
    status_name: data.status_name ?? status,
  };
}

/**
 * Some list endpoints expose `reviewed_date` before the single-prescription GET does.
 * Carry it into preview/detail data so review UI gating stays consistent.
 */
export function mergePrescriptionListFields(detail, listPrescription) {
  if (detail == null || typeof detail !== "object") {
    return normalizePafPrescription(detail);
  }
  if (listPrescription == null || typeof listPrescription !== "object") {
    return normalizePafPrescription(detail);
  }

  const merged = { ...detail };
  const reviewFields = [
    "reviewed_date",
    "review_outcome",
    "review_outcome_other",
    "reviewed_by",
    "reviewed_by_name",
    "reviewed_by_display",
  ];

  for (const field of reviewFields) {
    if (
      !Object.prototype.hasOwnProperty.call(merged, field) &&
      Object.prototype.hasOwnProperty.call(listPrescription, field)
    ) {
      merged[field] = listPrescription[field];
    }
  }

  return normalizePafPrescription(merged);
}

/** Human-readable PAF category from the URL segment (renew / create flows). */
export function typeOfFormLabelFromPathSegment(segment) {
  if (segment === "womanOfNCP") {
    return "Woman of Non-Childbearing Potential";
  }
  if (segment === "male") {
    return "Male";
  }
  return "Woman of Childbearing Potential";
}

/**
 * Resolves the patient category line shown in the summary (mirrors legacy ViewPAF logic).
 * @param {object} form
 * @param {string} [pathSegment3] - `pathname.split("/")[3]`
 */
export function resolvePatientCategoryLabel(form, pathSegment3) {
  if (form?.patient?.patient_type) {
    return form.patient.patient_type;
  }
  if (form?.patient_type) {
    return form.patient_type;
  }
  if (form?.type) {
    switch (form.type) {
      case "male":
        return "Male";
      case "womanOfNCP":
        return "Woman of Non-Childbearing Potential";
      case "womanOfCP":
        return "Woman of Childbearing Potential";
      default:
        return undefined;
    }
  }
  return typeOfFormLabelFromPathSegment(pathSegment3 ?? "");
}

export function extractIndication(form) {
  if (typeof form.indication === "string") {
    if (form?.indication?.includes("Other")) {
      return `Other (${form?.indication_other})`;
    }
    if (form?.indication?.includes("Clinical trial")) {
      return `Clinical Trial (${form?.clinical_trial})`;
    }
    return form?.indication;
  }

  if (typeof form.indication === "object") {
    if (form.indication?.label?.includes("Other")) {
      return `Other (${form?.indication_other})`;
    }
    if (form.indication?.label?.includes("Clinical trial")) {
      return `Clinical Trial (${form?.clinical_trial})`;
    }
    return form?.indication?.label;
  }
}

export function formatLastNegativePregnancyDate(form) {
  if (form?.pregnancy_test) {
    return moment(form.pregnancy_test).format("DD / MM / YYYY");
  }
  if (form?.patient?.pregnancy_test) {
    return moment(form.patient.pregnancy_test).format("DD / MM / YYYY");
  }
  return moment().format("DD / MM / YYYY");
}

export function markInRedPrescribedOnField(
  form,
  { isPatientInitiationOrRenew, pathSegment3 }
) {
  const typeOfFormVar = resolvePatientCategoryLabel(form, pathSegment3);

  if (typeOfFormVar !== "Woman of Childbearing Potential") {
    return false;
  }

  if (form?.status === "Approved") {
    return isDispensingMoreThan7DaysBetween2Dates(
      form?.pfa_issued,
      form?.accepted_date
    );
  }
  if (form?.status === "Rejected") {
    return isDispensingMoreThan7DaysBetween2Dates(
      form?.pfa_issued,
      form?.rejected_date
    );
  }

  if (isPatientInitiationOrRenew) {
    return false;
  }
  return isDispensingMoreThan7DaysBetween2Dates(form?.pfa_issued, new Date());
}

export function markInRedLastNegativePregnancyTest(form, isCreateNewPAF) {
  if (isCreateNewPAF) {
    return isDispensingMoreThan3DaysBetween2Dates(
      new Date(),
      form?.pregnancy_test
    );
  }
  return isDispensingMoreThan3DaysBetween2Dates(
    form?.pfa_issued,
    form?.pregnancy_test
  );
}

export function checkIfNumberOfCyclesExceeded(form) {
  const typeOfFormVar = form?.type ? form.type : form?.patient?.type;

  if (typeOfFormVar === typeOfPAF.WOMANOFCP) {
    const nrCycles = form.new_capsule_cycles
      ? form.new_capsule_cycles.id
      : form?.number_cycles?.id
        ? form.number_cycles.id
        : parseFloat(form?.n_cycles);

    return nrCycles > 1;
  }
  return false;
}

export function checkIfNumberOfWeeksExceeded(
  form,
  { isRenewal, typeOfForRenew, prescriptionField }
) {
  const typeOfFormVar = isRenewal
    ? typeOfForRenew
    : form?.type
      ? form.type
      : form?.patient?.type;

  const isWOMANOFCP = typeOfFormVar === typeOfPAF.WOMANOFCP;
  const isMALE = typeOfFormVar === typeOfPAF.MALE;
  const isWOMANOFNCP = typeOfFormVar === typeOfPAF.WOMANOFNCP;

  const maxWeeksWCBP =
    prescriptionField?.max_weeks_wcbp || form?.max_weeks_wcbp;
  const maxWeeksMOrWNCBP =
    prescriptionField?.max_weeks_m_or_wncbp || form?.max_weeks_m_or_wncbp;

  const nrCycles = form?.new_capsule_cycles?.id
    ? form.new_capsule_cycles.id
    : form?.number_weeks?.id
      ? form.number_weeks.id
      : form?.n_weeks_other
        ? form.n_weeks_other
        : form?.other
          ? parseFloat(form.other)
          : parseFloat(form?.n_weeks);

  return isWOMANOFCP
    ? maxWeeksWCBP && maxWeeksWCBP > 0
      ? nrCycles > maxWeeksWCBP
      : false
    : isMALE || isWOMANOFNCP
      ? maxWeeksMOrWNCBP && maxWeeksMOrWNCBP > 0
        ? nrCycles > maxWeeksMOrWNCBP
        : false
      : false;
}

export function checkIfTotalCapsulesExceeded(
  form,
  { isRenewal, typeOfForRenew, prescriptionField }
) {
  const typeOfFormVar = isRenewal
    ? typeOfForRenew
    : form?.type
      ? form.type
      : form?.patient?.type;

  if (
    form?.schedule_display_type === "weeks_per_cycle" ||
    prescriptionField?.schedule_display_type === "weeks_per_cycle"
  ) {
    const maxWeeksWcbp =
      form?.max_weeks_wcbp || prescriptionField?.max_weeks_wcbp;
    const maxWeeksMorWncbp =
      form?.max_weeks_m_or_wncbp || prescriptionField?.max_weeks_m_or_wncbp;
    const totalWeeksSupply =
      form?.new_total_weeks_supply || form?.total_weeks_supply;

    return typeOfFormVar === typeOfPAF.WOMANOFCP
      ? maxWeeksWcbp > 0 && totalWeeksSupply > maxWeeksWcbp
      : maxWeeksMorWncbp > 0 && totalWeeksSupply > maxWeeksMorWncbp;
  }

  if (
    form?.schedule_display_type === "weeks" ||
    prescriptionField?.schedule_display_type === "weeks"
  ) {
    const totalCapsules = form?.new_totalCapsules
      ? form.new_totalCapsules
      : form?.total_capsules;

    const maxCapsulesWcbp =
      form?.max_capsules_wcbp || prescriptionField?.max_capsules_wcbp;

    const maxCapsulesMorWncbp =
      form?.max_capsules_m_or_wncbp ||
      prescriptionField?.max_capsules_m_or_wncbp;

    return typeOfFormVar === typeOfPAF.WOMANOFCP
      ? maxCapsulesWcbp > 0 && totalCapsules > maxCapsulesWcbp
      : maxCapsulesMorWncbp > 0 && totalCapsules > maxCapsulesMorWncbp;
  }

  return false;
}

/**
 * Sorted copy of history strengths for amendment tooltips (stable ordering by strength).
 */
export function getSortedCapsuleStrengthHistory(form) {
  return form?.history?.capsule_changes_tracker
    ?.slice()
    .sort(
      (a, b) => parseFloat(a?.strength_value) - parseFloat(b?.strength_value)
    );
}

/**
 * Computes aggregate capsule / week fields after strength or cycle edits.
 * Returns `null` when there is nothing to update.
 */
export function buildTotalCapsulesStateUpdate(form, displayWeeks) {
  if (!form?.strengths) {
    return null;
  }

  const capsuleValues = form.strengths.map((element) => {
    if (
      element?.new_capsules &&
      typeof element.new_capsules !== "string"
    ) {
      return parseFloat(element.new_capsules);
    }
    if (element?.new_capsules_other) {
      return element.new_capsules_other;
    }
    return element.capsules;
  });

  const sumOfValues = capsuleValues.reduce((acc, cur) => acc + cur, 0);

  const numberOfCyles = form?.new_capsule_cycles
    ? form.new_capsule_cycles.id
    : parseFloat(form?.n_cycles);

  const numberOfWeeks =
    form?.new_capsule_cycles?.id && form?.new_capsule_cycles?.id !== 0
      ? form.new_capsule_cycles.id
      : form?.n_weeks_other
        ? form.n_weeks_other
        : parseFloat(form?.n_weeks);

  const totalWeeksSupply =
    form?.schedule_display_type === "weeks_per_cycle"
      ? (form?.new_capsule_cycles?.id
          ? form.new_capsule_cycles.id
          : parseInt(form?.n_cycles?.match(/\d+/)[0], 10)) *
        (form.new_weeks_per_cycle?.id === 0
          ? form?.new_weeks_per_cycle_other
          : form?.new_weeks_per_cycle?.id || form.weeks_per_cycle)
      : null;

  const totalCapsules =
    +sumOfValues * (displayWeeks ? numberOfWeeks / 4 : numberOfCyles || 0);

  return {
    capsule_strength: sumOfValues,
    new_totalCapsules: isNaN(totalCapsules) ? 0 : Math.ceil(totalCapsules),
    total_capsules: isNaN(totalCapsules) ? 0 : Math.ceil(totalCapsules),
    total_weeks_supply: totalWeeksSupply || 0,
  };
}
